/**
 * Credits hooks — Chunk 3
 *
 * Wraps two-phase credit reservation RPCs:
 *   - reserve_credits → useReserveCredits (mutation)
 *   - confirm_credits → useConfirmCredits (mutation)
 *   - get_credit_balance → useCreditBalance (query)
 *   - enroll_with_free_credits → useEnrollWithFreeCredits (mutation)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { normalizeError } from '@/lib/error-types';
import { queryKeys } from '@/utils/queryKeys';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import type { Database } from '@/integrations/supabase/types';

type ReserveCreditsArgs = Database['public']['Functions']['reserve_credits']['Args'];
type ReserveCreditsReturns = Database['public']['Functions']['reserve_credits']['Returns'];
type ConfirmCreditsArgs = Database['public']['Functions']['confirm_credits']['Args'];
type GetCreditBalanceReturns = Database['public']['Functions']['get_credit_balance']['Returns'];
type EnrollWithFreeCreditsArgs = Database['public']['Functions']['enroll_with_free_credits']['Args'];

export interface CreditBalance {
  granted: number;
  consumed: number;
  /** Sum of non-finalized pending reservations within the 5-min TTL window. */
  pending: number;
  /** granted - consumed - pending, floored at 0. */
  available: number;
}

export interface CreditReservation {
  reservation_id: string;
  reserved: number;
  available: number;
}

/** Reserve credits before LLM call (phase 1 of two-phase commit) */
export function useReserveCredits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: ReserveCreditsArgs): Promise<CreditReservation> => {
      const { data, error } = await supabase.rpc('reserve_credits', args);
      if (error) throw normalizeError(error);
      return data as unknown as CreditReservation;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.credits.balance(variables.p_student_id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.credits.reservations() });
    },
    throwOnError: true,
  });
}

/** Confirm consumed credits after LLM call (phase 2 of two-phase commit) */
export function useConfirmCredits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: ConfirmCreditsArgs): Promise<void> => {
      const { error } = await supabase.rpc('confirm_credits', args);
      if (error) throw normalizeError(error);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.credits.balance(variables.p_student_id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.credits.reservations() });
    },
    throwOnError: true,
  });
}

/** Current credit balance for a student */
export function useCreditBalance(studentId: string) {
  const queryClient = useQueryClient();

  // GAP-79: realtime subscription on interaction_costs INSERTs for this
  // student. When the Edge Function confirms a charge, the client sees
  // balance change within milliseconds instead of waiting for staleTime
  // poll. staleTime kept long (5min) because realtime is now the primary
  // invalidation path; poll is the fallback for missed events.
  useRealtimeSubscription({
    channelName: `credits-${studentId}`,
    table: 'interaction_costs',
    eventType: 'INSERT',
    filter: studentId ? `student_id=eq.${studentId}` : undefined,
    enabled: !!studentId,
    onEvent: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.credits.balance(studentId) });
    },
  });

  return useQuery({
    queryKey: queryKeys.credits.balance(studentId),
    queryFn: async (): Promise<CreditBalance> => {
      const { data, error } = await supabase.rpc('get_credit_balance', { p_student_id: studentId });
      if (error) throw normalizeError(error);
      return data as unknown as CreditBalance;
    },
    enabled: !!studentId,
    staleTime: 300_000, // 5min — realtime is primary; poll is fallback
    // GAP-38: this hook powers the header credit badge — non-essential UI.
    // A balance fetch failure must NOT cascade into the global ErrorBoundary
    // and take down the entire layout (Header → Sidebar etc). The badge
    // shows '—' on error.
    throwOnError: false,
  });
}

/** Enroll in a free subject and seed monthly credits */
export function useEnrollWithFreeCredits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: EnrollWithFreeCreditsArgs): Promise<void> => {
      const { error } = await supabase.rpc('enroll_with_free_credits', args);
      if (error) throw normalizeError(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credits'] });
      // Invalidate BOTH keys — the landing page reads my_courses via the L5
      // batched `get_classroom_landing_state` (keyed to landingState()), while
      // non-landing consumers of useMyCourses (LearningAnalytics, Catalogue,
      // usePlatformConfig) still read from the standalone myCourses() key.
      // Missing either invalidation = stale courses after free-credit enroll.
      queryClient.invalidateQueries({ queryKey: queryKeys.classroom.landingState() });
      queryClient.invalidateQueries({ queryKey: queryKeys.classroom.myCourses() });
    },
    throwOnError: true,
  });
}

// Re-export RPC argument/return types for callers
export type { ReserveCreditsArgs, ReserveCreditsReturns, ConfirmCreditsArgs, GetCreditBalanceReturns };
